import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { ButtonEnums } from '../button';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
   AlignSelfValuesMap,
   Sizes,
   Variant,
   VariantValuesMap,
} from '../enum-types';
import { classMap } from 'lit/directives/class-map.js';
import { AtlAriaIconProps, IconSource } from '../icon/icon.props';
import { IClassMap } from '../helpers';
import classNames from 'classnames';
import {
   ActionListGroupItemButtonProps,
   ActionListGroupItemIconProps,
   ActionListGroupItemProps,
   ActionStatusProps,
   AtlListGroupTagNames,
   ListGroupItemButtonClickEvent,
} from './list-group.props';

@customElement(AtlListGroupTagNames.ActionListGroupItem)
export class AtlActionListGroupItem
   extends LitElement
   implements ActionListGroupItemProps
{
   declare itemTitle?: string;
   declare actionButton?: ActionListGroupItemButtonProps;
   declare icon?: ActionListGroupItemIconProps;
   declare status?: ActionStatusProps;
   declare categories?: string[];
   declare truncateText?: boolean;
   declare actionTextClass?: string;
   declare isClickable?: boolean;
   declare variant?: Variant;

   static properties = {
      itemTitle: { attribute: 'item-title', type: String },
      actionButton: { type: Object },
      icon: { type: Object },
      status: { type: Object },
      categories: { type: Array },
      truncateText: { attribute: 'truncate-text', type: Boolean },
      actionTextClass: { attribute: 'action-text-class', type: String },
      isClickable: { type: Boolean },
      variant: { type: Variant },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
            width: 100%;
         }
      `
   ];

   private _renderIconContent() {
      if (!this.icon) return nothing;

      const iconClassNames = ['icon'];

      if (this.icon.bordered) {
         iconClassNames.push('icon-bordered');
      }

      if (this.icon.className) {
         this.icon.className.split(' ').forEach((className) => {
            iconClassNames.push(className);
         });
      }

      if (this.icon.imageSrc) {
         return html`<img
            src=${this.icon.imageSrc}
            class=${iconClassNames.join(' ')}
            alt=${ifDefined(this.icon.altText)}
         />`;
      }

      return html`<atlantis-icon
         name=${ifDefined(this.icon.iconName)}
         iconSource=${IconSource.FontAwesome}
         iconClassName=${iconClassNames.join(' ')}
         .iconAriaSettings=${{ ariaHidden: true } as AtlAriaIconProps}
      >
      </atlantis-icon>`;
   }

   override updated(changedProperties: PropertyValues) {
      if (changedProperties.has('icon')) {
         this._updateAlignCLass();
      }
   }
   private _handleIconSlotChange(event: Event) {
      const slot = event.target as HTMLSlotElement;
      const assignedNodes = slot.assignedNodes({ flatten: false });

      if (assignedNodes.length > 0) {
         const wrapper = document.createElement('div');

         wrapper.classList.add('action-icon');
         if (this.icon?.align) {
            wrapper.classList.add(AlignSelfValuesMap[this.icon.align]);
            wrapper.classList.add('h-auto');
         }

         assignedNodes.forEach((node) => {
            wrapper.appendChild(node);
         });

         slot.parentNode?.insertBefore(wrapper, slot);
         slot.remove();
      }
   }

   private _updateAlignCLass = () => {
      const actionIconContainer =
         this.shadowRoot?.querySelector('.action-icon');
      if (actionIconContainer && this.icon?.align) {
         actionIconContainer.classList.add(AlignSelfValuesMap[this.icon.align]);
         actionIconContainer.classList.add('h-auto');
         actionIconContainer.classList.add('m-0');
      }
   };

   private _renderIcon() {
      if (!this.icon || (!this.icon.iconName && !this.icon.imageSrc)) {
         return html`<slot
            name="icon"
            @slotchange=${this._handleIconSlotChange}
         ></slot>`;
      }

      const classNames: { [key: string]: boolean } = {
         'action-icon': true,
         [VariantValuesMap[this.variant!]]: true,
      };

      if (this.icon.align) {
         classNames[AlignSelfValuesMap[this.icon.align]] = true;
         classNames['h-auto'] = true;
         classNames['m-0'] = true;
      }

      if (this.icon.containerClassName) {
         this.icon.containerClassName.split(' ').forEach((className) => {
            classNames[className] = true;
         });
      }

      let iconContent = this._renderIconContent();
      if (this.icon.bordered) {
         iconContent = html`<div class="bordered">${iconContent}</div>`;
      }
      return html`<div class=${classMap(classNames)}>${iconContent}</div>`;
   }

   private _renderActionButton() {
      if (!this.actionButton) return nothing;

      return html`
         <div class="action-cta">
            <atlantis-button
               variant=${ButtonEnums.ButtonVariant.Secondary}
               iconName=${ifDefined(this.actionButton.iconName)}
               className=${ifDefined(this.actionButton.buttonClassName)}
               fullWidth
               class="w-100"
               @click=${() =>
                  this.dispatchEvent(
                     new CustomEvent(ListGroupItemButtonClickEvent, {
                        bubbles: true,
                        composed: true,
                     })
                  )}
            >
               ${this.actionButton.text}
            </atlantis-button>
         </div>
      `;
   }

   private _renderCategories() {
      if (!this.categories || this.categories.length === 0) return nothing;

      return html`
         <div class="action-categories">
            ${this.categories.map(
               (category) =>
                  html`<atlantis-chip
                     variant=${Variant.SecondaryBordered}
                     label=${category}
                     size=${Sizes.Medium}
                  ></atlantis-chip>`
            )}
         </div>
      `;
   }

   private _renderStatus() {
      if (!this.status || (!this.status.variant && !this.status.dueDate))
         return nothing;

      return html`<div class="action-status">
         ${this.status.dueDate
            ? html`<span class="status-due-date"
                 >Due Date <strong>${this.status.dueDate}</strong></span
              >`
            : nothing}
         ${this.status.variant
            ? html`<atlantis-status-chip
                 variant=${this.status.variant}
                 label=${ifDefined(this.status.label)}
                 size=${Sizes.Medium}
                 show-avatar-icon
                 avatar-icon-name=${ifDefined(this.status.icon)}
              >
              </atlantis-status-chip>`
            : nothing}
      </div>`;
   }

   render() {
      const actionTextClasses: IClassMap = {
         'action-text': true,
         'text-truncate': this.truncateText == true,
      };

      if (this.actionTextClass) {
         actionTextClasses[this.actionTextClass] = true;
      }

      return html`
         <div
            class=${classNames('list-group-item', {
               clickable: this.isClickable,
            })}
            tabindex="${ifDefined(this.isClickable ? 0 : undefined)}"
         >
            ${this._renderIcon()}
            <div
               class=${classNames('action-body', {
                  'action-body-wrapper': this.actionButton,
               })}
            >
               ${this.itemTitle
                  ? html`<div class="action-title">${this.itemTitle}</div>`
                  : nothing}
               ${this._renderStatus()}
               <div class=${classMap(actionTextClasses)}>
                  <slot></slot>
               </div>
               ${this._renderCategories()}
            </div>
            ${this._renderActionButton()}
         </div>
      `;
   }
}
